import CodeIcon from '@mui/icons-material/Code'
import FormatBoldIcon from '@mui/icons-material/FormatBold'
import FormatItalicIcon from '@mui/icons-material/FormatItalic'
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted'
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered'
import FormatQuoteIcon from '@mui/icons-material/FormatQuote'
import FormatStrikethroughIcon from '@mui/icons-material/FormatStrikethrough'
import HorizontalRuleIcon from '@mui/icons-material/HorizontalRule'
import ImageIcon from '@mui/icons-material/Image'
import RedoIcon from '@mui/icons-material/Redo'
import UndoIcon from '@mui/icons-material/Undo'
import YouTubeIcon from '@mui/icons-material/YouTube'
import { Container } from '@mui/material'
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Divider, Paper, TextField, Toolbar } from '@mui/material'
import Image from '@tiptap/extension-image'
import { useEditor, EditorContent, FloatingMenu, BubbleMenu } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import { debounce } from 'lodash'
import React, { useEffect, useRef, useState } from 'react'
import { Markdown } from 'tiptap-markdown'

import { YouTube } from './extensions/YouTube'
import { getYouTubeEmbedUrl } from './extensions/YouTubeNodeView'
import ImageService from '../../services/ImageService'
import ImageUploader from '../common/ImageUploader'

// 自定义 Image 扩展，添加 resourceId 属性用于跟踪服务器上的图片
const CustomImage = Image.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      src: {
        default: null,
      },
      alt: {
        default: null,
      },
      title: {
        default: null,
      },
      // 添加 resourceId 属性，用于在删除时从服务器删除图片
      resourceId: {
        default: null,
        parseHTML: (element) => element.getAttribute('data-resource-id'),
        renderHTML: (attributes) => {
          if (!attributes.resourceId) {
            return {}
          }
          return {
            'data-resource-id': attributes.resourceId,
          }
        },
      },
    }
  },
})

// define your extension array - 移到组件外部，避免每次渲染都创建新数组
const extensions = [StarterKit, Markdown, YouTube, CustomImage]

/**
 * MenuButton Component
 * 统一的工具栏按钮组件，共享相同的样式
 * @param {boolean} isActive - 按钮是否处于激活状态
 * @param {function} onClick - 点击回调函数
 * @param {string} title - 工具提示文本
 * @param {boolean} disabled - 是否禁用
 * @param {React.ReactNode} children - 按钮内容
 * @param {object} sx - 额外的样式覆盖
 */
const MenuButton = ({ isActive = false, onClick, title, disabled = false, children, sx = {} }) => {
  return (
    <Button
      size="small"
      variant="text"
      onClick={onClick}
      disabled={disabled}
      title={title}
      sx={{
        minWidth: '28px',
        px: 0.5,
        py: 0.25,
        bgcolor: isActive ? 'action.selected' : 'transparent',
        '&:hover': {
          bgcolor: 'action.hover',
        },
        ...sx,
      }}
    >
      {children}
    </Button>
  )
}

/**
 * TiptapEditor Component with Markdown support
 * @param {string} defaultContent - Markdown string to initialize the editor with
 * @param {function} onChange - Callback function called when content changes, receives markdown string
 */
const Tiptap = ({ defaultContent = '', onChange }) => {
  // 使用 ref 保存初始 markdown 值，确保只在首次渲染时使用
  const initialMarkdownRef = useRef(defaultContent)
  const hasInitializedRef = useRef(false)
  const [youtubeDialogOpen, setYoutubeDialogOpen] = useState(false)
  const [youtubeUrl, setYoutubeUrl] = useState('')
  const [youtubeError, setYoutubeError] = useState('')
  const [imageUploadDialogOpen, setImageUploadDialogOpen] = useState(false)

  // 使用 useRef 保存 onChange，避免在 onUpdate 中使用过期的闭包
  const onChangeRef = useRef(onChange)
  useEffect(() => {
    onChangeRef.current = onChange
  }, [onChange])

  // 创建防抖的 onChange 函数，延迟 300ms 执行以改善输入体验
  const debouncedOnChangeRef = useRef(null)
  useEffect(() => {
    // 创建防抖函数
    debouncedOnChangeRef.current = debounce((markdownContent) => {
      const currentOnChange = onChangeRef.current
      if (currentOnChange) {
        currentOnChange(markdownContent)
      }
    }, 400)

    // 清理函数：组件卸载时取消待执行的防抖调用
    return () => {
      if (debouncedOnChangeRef.current) {
        debouncedOnChangeRef.current.cancel()
      }
    }
  }, [])

  // 使用 ref 保存编辑器实例，以便在 handlePaste 中访问
  const editorRef = useRef(null)

  const editor = useEditor({
    extensions,
    content: '', // 初始为空，稍后通过 onCreate 设置 markdown 内容
    // 性能优化：防止每次事务都重新渲染
    shouldRerenderOnTransaction: false,
    // 添加 editorProps 来处理粘贴事件，支持粘贴 Markdown 文本
    editorProps: {
      handlePaste: (view, event, _slice) => {
        // 获取编辑器实例
        const currentEditor = editorRef.current
        if (!currentEditor) return false

        // 获取剪贴板数据
        const clipboardData = event.clipboardData || window.clipboardData
        if (!clipboardData) return false

        // 检查是否有 HTML 内容
        const htmlData = clipboardData.getData('text/html')
        const textData = clipboardData.getData('text/plain')

        // 检查是否是 Ctrl+Shift+V 或 Cmd+Shift+V（强制粘贴为 Markdown）
        const isForceMarkdown = event.shiftKey && (event.ctrlKey || event.metaKey)

        // 辅助函数：将 Markdown 转换为 JSON 格式
        const convertMarkdownToJSON = (markdownText) => {
          // 保存当前编辑器内容
          const currentContent = currentEditor.getJSON()
          try {
            // 使用 setContent 将 Markdown 转换为 JSON
            currentEditor.commands.setContent(markdownText, false, {
              contentType: 'markdown',
            })
            const jsonContent = currentEditor.getJSON()
            // 恢复原内容
            currentEditor.commands.setContent(currentContent)
            return jsonContent
          } catch (error) {
            // 如果出错，恢复原内容
            currentEditor.commands.setContent(currentContent)
            throw error
          }
        }

        // 如果用户按了 Ctrl+Shift+V，强制作为 Markdown 处理
        if (isForceMarkdown && textData && textData.trim()) {
          event.preventDefault()
          try {
            const { from, to } = view.state.selection
            const markdownContent = textData.trim()
            // 将 Markdown 转换为 JSON
            const jsonContent = convertMarkdownToJSON(markdownContent)
            // 删除选中的内容（如果有），然后插入转换后的内容
            if (from !== to) {
              currentEditor.commands.deleteRange({ from, to })
            }
            // 插入转换后的内容
            currentEditor.commands.insertContentAt(from, jsonContent)
            return true // 表示已处理粘贴事件
          } catch (error) {
            console.error('Error pasting markdown:', error)
            // 如果解析失败，回退到默认行为
            return false
          }
        }

        // 如果没有 HTML 内容，尝试作为 Markdown 处理
        if (!htmlData || !htmlData.trim()) {
          if (textData && textData.trim()) {
            event.preventDefault()
            try {
              const { from, to } = view.state.selection
              const markdownContent = textData.trim()
              // 将 Markdown 转换为 JSON
              const jsonContent = convertMarkdownToJSON(markdownContent)
              // 删除选中的内容（如果有），然后插入转换后的内容
              if (from !== to) {
                currentEditor.commands.deleteRange({ from, to })
              }
              // 插入转换后的内容
              currentEditor.commands.insertContentAt(from, jsonContent)
              return true // 表示已处理粘贴事件
            } catch (error) {
              console.error('Error pasting markdown:', error)
              // 如果解析失败，回退到默认行为
              return false
            }
          }
        }

        return false // 使用默认行为处理 HTML
      },
    },
    onCreate: ({ editor }) => {
      // 保存编辑器引用，以便在 handlePaste 中使用
      editorRef.current = editor
      // 如果提供了 markdown，在初始化后设置内容（仅执行一次）
      if (initialMarkdownRef.current && !hasInitializedRef.current) {
        try {
          // tiptap-markdown 支持通过 setContent 设置 markdown
          editor.commands.setContent(initialMarkdownRef.current)
          hasInitializedRef.current = true
        } catch (error) {
          console.error('Error parsing markdown:', error)
          hasInitializedRef.current = true
        }
      }
    },
    onUpdate: ({ editor }) => {
      // 当内容更新时，获取 markdown 格式并通过防抖函数调用 onChange
      // 使用防抖来减少频繁调用，改善输入体验
      const debouncedOnChange = debouncedOnChangeRef.current
      if (debouncedOnChange) {
        try {
          // tiptap-markdown 提供 getMarkdown 方法
          let markdownContent = ''

          // 方法1: 尝试使用 editor.getMarkdown() (如果扩展提供了这个方法)
          if (typeof editor.getMarkdown === 'function') {
            markdownContent = editor.getMarkdown()
          }
          // 方法2: 尝试使用 storage.markdown.getMarkdown()
          else if (editor.storage?.markdown?.getMarkdown) {
            markdownContent = editor.storage.markdown.getMarkdown()
          }
          // 方法3: 尝试从 Markdown 扩展的 storage 中获取
          else {
            // 获取 Markdown 扩展实例
            const markdownExtension = editor.extensionManager?.extensions?.find((ext) => ext.name === 'markdown')
            if (markdownExtension?.storage?.getMarkdown) {
              markdownContent = markdownExtension.storage.getMarkdown(editor)
            }
          }

          // 如果所有方法都失败，使用 JSON 作为后备
          if (!markdownContent) {
            console.warn('Markdown 扩展未提供 getMarkdown 方法，使用 JSON 作为后备')
            markdownContent = JSON.stringify(editor.getJSON())
          }

          // 使用防抖函数调用 onChange，而不是直接调用
          debouncedOnChange(markdownContent)
        } catch (error) {
          console.error('Error converting to markdown:', error)
          // 发生错误时，使用 JSON 作为后备
          try {
            const jsonContent = JSON.stringify(editor.getJSON())
            debouncedOnChange(jsonContent)
          } catch (jsonError) {
            console.error('Error getting JSON as fallback:', jsonError)
          }
        }
      }
    },
  })

  // 当 defaultContent 从外部更新时，更新编辑器内容
  useEffect(() => {
    if (editor && defaultContent !== initialMarkdownRef.current && hasInitializedRef.current) {
      try {
        editor.commands.setContent(defaultContent)
        initialMarkdownRef.current = defaultContent
      } catch (error) {
        console.error('Error updating editor content:', error)
      }
    }
  }, [defaultContent, editor])

  /**
   * 处理插入 YouTube 视频
   */
  const handleInsertYoutube = () => {
    setYoutubeError('')
    const embedUrl = getYouTubeEmbedUrl(youtubeUrl)

    if (!embedUrl) {
      setYoutubeError('Please enter a valid YouTube video URL')
      return
    }

    editor.commands.setYoutubeVideo({
      src: embedUrl,
    })
    setYoutubeUrl('')
    setYoutubeDialogOpen(false)
    editor.commands.focus()
  }

  /**
   * 处理打开 YouTube 对话框
   */
  const handleOpenYoutubeDialog = () => {
    setYoutubeUrl('')
    setYoutubeError('')
    setYoutubeDialogOpen(true)
  }

  /**
   * 处理关闭 YouTube 对话框
   */
  const handleCloseYoutubeDialog = () => {
    setYoutubeUrl('')
    setYoutubeError('')
    setYoutubeDialogOpen(false)
  }

  /**
   * 处理图片上传成功
   */
  const handleImageUploadSuccess = (imageData) => {
    // 插入图片到编辑器
    editor.commands.setImage({
      src: imageData.r2Url,
      alt: imageData.originalName || 'Uploaded image',
      resourceId: imageData.resourceId, // 保存 resourceId 以便删除时使用
    })

    // 关闭对话框并聚焦编辑器
    setImageUploadDialogOpen(false)
    editor.commands.focus()
  }

  /**
   * 处理打开图片上传对话框
   */
  const handleOpenImageDialog = () => {
    setImageUploadDialogOpen(true)
  }

  /**
   * 处理关闭图片上传对话框
   */
  const handleCloseImageDialog = () => {
    setImageUploadDialogOpen(false)
  }

  /**
   * 处理图片删除（当从编辑器删除图片时，从服务器删除）
   * 通过监听事务来检测被删除的图片节点
   */
  useEffect(() => {
    if (!editor) return

    // 存储当前文档中所有图片的 resourceId
    const currentImageIds = new Set()

    // 更新当前图片 ID 集合
    const updateImageIds = () => {
      currentImageIds.clear()
      editor.state.doc.descendants((node) => {
        if (node.type.name === 'image' && node.attrs.resourceId) {
          currentImageIds.add(node.attrs.resourceId)
        }
      })
    }

    // 初始化
    updateImageIds()

    // 监听事务完成事件
    const handleTransaction = ({ transaction }) => {
      // 检查是否有内容变化
      if (!transaction.docChanged) return

      // 获取新文档中的图片 ID
      const newImageIds = new Set()
      transaction.doc.descendants((node) => {
        if (node.type.name === 'image' && node.attrs.resourceId) {
          newImageIds.add(node.attrs.resourceId)
        }
      })

      // 找出被删除的图片（在当前集合中但不在新集合中）
      const deletedImageIds = []
      currentImageIds.forEach((id) => {
        if (!newImageIds.has(id)) {
          deletedImageIds.push(id)
        }
      })

      // 删除服务器上的图片
      deletedImageIds.forEach((resourceId) => {
        ImageService.deleteImage(resourceId).catch((error) => {
          console.error('Failed to delete image from server:', error)
          // 不显示错误给用户，因为图片已经从编辑器中删除了
        })
      })

      // 更新当前图片 ID 集合
      updateImageIds()
    }

    editor.on('transaction', handleTransaction)

    return () => {
      // 清理事件监听器
      editor.off('transaction', handleTransaction)
    }
  }, [editor])

  if (!editor) {
    return null
  }

  return (
    <Container
      disableGutters
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        boxShadow: 1,
        textAlign: 'center',
        backgroundColor: 'background.paper',
        padding: 1,
        '&:focus-within': {
          outline: '2px solid',
          outlineColor: 'primary.main',
        },
        '& .ProseMirror': {
          outline: 'none',
          minHeight: '250px',
          '& p': {
            margin: '0.5em 0',
          },
          '& h1, & h2, & h3': {
            marginTop: '1em',
            marginBottom: '0.5em',
          },
          '& ul, & ol': {
            paddingLeft: '1.5em',
          },
          '& img': {
            maxWidth: '100%',
            height: 'auto',
            margin: '1em 0',
            borderRadius: '4px',
            cursor: 'pointer',
            '&.ProseMirror-selectednode': {
              outline: '2px solid',
              outlineColor: 'primary.main',
            },
          },
        },
      }}
    >
      {/* 顶部工具栏 */}
      <Paper
        elevation={0}
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: '4px 4px 0 0',
        }}
      >
        <Toolbar
          variant="dense"
          sx={{
            minHeight: '32px !important',
            gap: 0.25,
            flexWrap: 'wrap',
            px: 0.5,
            py: 0.25,
          }}
        >
          {/* 撤销/重做 */}
          <MenuButton onClick={() => editor.chain().focus().undo().run()} disabled={!editor.can().undo()} title="Undo (Ctrl+Z)">
            <UndoIcon fontSize="small" />
          </MenuButton>
          <MenuButton onClick={() => editor.chain().focus().redo().run()} disabled={!editor.can().redo()} title="Redo (Ctrl+Y)">
            <RedoIcon fontSize="small" />
          </MenuButton>

          <Divider orientation="vertical" flexItem sx={{ mx: 0.25, my: 0.5 }} />

          {/* 标题 */}
          <MenuButton isActive={editor.isActive('heading', { level: 1 })} onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} title="Heading 1">
            H1
          </MenuButton>
          <MenuButton isActive={editor.isActive('heading', { level: 2 })} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} title="Heading 2">
            H2
          </MenuButton>
          <MenuButton isActive={editor.isActive('heading', { level: 3 })} onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} title="Heading 3">
            H3
          </MenuButton>

          <Divider orientation="vertical" flexItem sx={{ mx: 0.25, my: 0.5 }} />

          {/* 文本格式 */}
          <MenuButton isActive={editor.isActive('bold')} onClick={() => editor.chain().focus().toggleBold().run()} title="Bold (Ctrl+B)">
            <FormatBoldIcon fontSize="small" />
          </MenuButton>
          <MenuButton isActive={editor.isActive('italic')} onClick={() => editor.chain().focus().toggleItalic().run()} title="Italic (Ctrl+I)">
            <FormatItalicIcon fontSize="small" />
          </MenuButton>
          <MenuButton isActive={editor.isActive('strike')} onClick={() => editor.chain().focus().toggleStrike().run()} title="Strikethrough">
            <FormatStrikethroughIcon fontSize="small" />
          </MenuButton>
          <MenuButton isActive={editor.isActive('code')} onClick={() => editor.chain().focus().toggleCode().run()} title="Inline Code">
            <CodeIcon fontSize="small" />
          </MenuButton>

          <Divider orientation="vertical" flexItem sx={{ mx: 0.25, my: 0.5 }} />

          {/* 列表 */}
          <MenuButton isActive={editor.isActive('bulletList')} onClick={() => editor.chain().focus().toggleBulletList().run()} title="Bullet List">
            <FormatListBulletedIcon fontSize="small" />
          </MenuButton>
          <MenuButton isActive={editor.isActive('orderedList')} onClick={() => editor.chain().focus().toggleOrderedList().run()} title="Ordered List">
            <FormatListNumberedIcon fontSize="small" />
          </MenuButton>

          <Divider orientation="vertical" flexItem sx={{ mx: 0.25, my: 0.5 }} />

          {/* 引用和分隔线 */}
          <MenuButton isActive={editor.isActive('blockquote')} onClick={() => editor.chain().focus().toggleBlockquote().run()} title="Blockquote">
            <FormatQuoteIcon fontSize="small" />
          </MenuButton>
          <MenuButton onClick={() => editor.chain().focus().setHorizontalRule().run()} title="Horizontal Rule">
            <HorizontalRuleIcon fontSize="small" />
          </MenuButton>

          <Divider orientation="vertical" flexItem sx={{ mx: 0.25, my: 0.5 }} />

          {/* YouTube 视频 */}
          <MenuButton onClick={handleOpenYoutubeDialog} title="Insert YouTube Video">
            <YouTubeIcon fontSize="small" />
          </MenuButton>

          <Divider orientation="vertical" flexItem sx={{ mx: 0.25, my: 0.5 }} />

          {/* 图片上传 */}
          <MenuButton onClick={handleOpenImageDialog} title="Insert Image">
            <ImageIcon fontSize="small" />
          </MenuButton>
        </Toolbar>
      </Paper>

      {/* 图片上传对话框 */}
      <Dialog open={imageUploadDialogOpen} onClose={handleCloseImageDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Upload Image</DialogTitle>
        <DialogContent>
          <ImageUploader onUploadSuccess={handleImageUploadSuccess} buttonText="Select Image" dragText="Drag and drop an image here, or click to select" sx={{ mt: 2 }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseImageDialog}>Cancel</Button>
        </DialogActions>
      </Dialog>

      {/* YouTube 插入对话框 */}
      <Dialog open={youtubeDialogOpen} onClose={handleCloseYoutubeDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Insert YouTube Video</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="YouTube Video URL"
            fullWidth
            variant="outlined"
            value={youtubeUrl}
            onChange={(e) => {
              setYoutubeUrl(e.target.value)
              setYoutubeError('')
            }}
            error={!!youtubeError}
            helperText={youtubeError || 'Supports standard YouTube URL or youtu.be short link'}
            placeholder="https://www.youtube.com/watch?v=VIDEO_ID or https://youtu.be/VIDEO_ID"
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseYoutubeDialog}>Cancel</Button>
          <Button onClick={handleInsertYoutube} variant="contained">
            Insert
          </Button>
        </DialogActions>
      </Dialog>

      {/* 编辑器内容区域 */}
      <Box
        sx={{
          border: 'none',
          borderColor: 'divider',
          borderRadius: '0 0 4px 4px',
        }}
      >
        <EditorContent editor={editor} />
      </Box>
      <FloatingMenu
        editor={editor}
        shouldShow={({ state }) => {
          const { selection } = state
          const { empty, from } = selection

          // 只在插入模式（空选择）时显示
          if (!empty) {
            return false
          }

          // 检查光标是否在块级节点中（如图片、YouTube 视频）
          const $from = state.doc.resolve(from)
          const node = $from.node()

          // 如果光标在块级节点中，不显示
          if (node && (node.type.name === 'image' || node.type.name === 'youtube')) {
            return false
          }

          // 检查父节点是否是块级节点
          const parent = $from.parent
          if (parent && (parent.type.name === 'image' || parent.type.name === 'youtube')) {
            return false
          }

          // 检查光标是否在行首
          // $from.parentOffset === 0 表示光标在父节点的开始位置
          // $from.start() === from 表示光标在节点的开始位置
          const isAtStartOfNode = $from.parentOffset === 0
          const isAtStartOfLine = $from.start() === from

          // 只在行首时显示
          return isAtStartOfNode && isAtStartOfLine
        }}
      >
        <Box
          sx={{
            bgcolor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            p: 0.5,
            boxShadow: 2,
            display: 'flex',
            gap: 0.5,
          }}
        >
          <MenuButton isActive={editor.isActive('heading', { level: 1 })} onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} sx={{ minWidth: 'auto', px: 1 }}>
            H1
          </MenuButton>
          <MenuButton isActive={editor.isActive('heading', { level: 2 })} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} sx={{ minWidth: 'auto', px: 1 }}>
            H2
          </MenuButton>
        </Box>
      </FloatingMenu>
      <BubbleMenu
        editor={editor}
        tippyOptions={{ duration: 100 }}
        shouldShow={({ state }) => {
          const { selection } = state
          const { empty, from, to } = selection

          // 如果没有选中任何内容，不显示
          if (empty) {
            return false
          }

          // 检查选中的内容大小
          const selectedContent = state.doc.slice(from, to)
          if (!selectedContent || selectedContent.size === 0) {
            return false
          }

          // 检查是否选中了图片或 YouTube 视频节点（这些节点不应该显示 BubbleMenu）
          let hasBlockNode = false
          state.doc.nodesBetween(from, to, (node) => {
            // 如果选中了块级节点（如图片、YouTube 视频），不显示 BubbleMenu
            if (node.isBlock && (node.type.name === 'image' || node.type.name === 'youtube')) {
              hasBlockNode = true
            }
          })

          if (hasBlockNode) {
            return false
          }

          // 只在选中文本内容时显示
          return selectedContent.content.size > 0
        }}
      >
        <Box
          sx={{
            bgcolor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            p: 0.5,
            boxShadow: 3,
            display: 'flex',
            gap: 0.5,
          }}
        >
          <MenuButton isActive={editor.isActive('bold')} onClick={() => editor.chain().focus().toggleBold().run()} sx={{ minWidth: 'auto', px: 1 }}>
            <FormatBoldIcon fontSize="small" />
          </MenuButton>
          <MenuButton isActive={editor.isActive('italic')} onClick={() => editor.chain().focus().toggleItalic().run()} sx={{ minWidth: 'auto', px: 1 }}>
            <FormatItalicIcon fontSize="small" />
          </MenuButton>
          <MenuButton isActive={editor.isActive('strike')} onClick={() => editor.chain().focus().toggleStrike().run()} sx={{ minWidth: 'auto', px: 1 }}>
            <FormatStrikethroughIcon fontSize="small" />
          </MenuButton>
          <MenuButton isActive={editor.isActive('code')} onClick={() => editor.chain().focus().toggleCode().run()} sx={{ minWidth: 'auto', px: 1 }}>
            <CodeIcon fontSize="small" />
          </MenuButton>
        </Box>
      </BubbleMenu>
    </Container>
  )
}

// 使用 React.memo 包装组件，避免不必要的重新渲染
// 只有当 defaultContent 真正发生变化时才重新渲染
// 完全忽略 onChange 的变化，因为我们使用 ref 来访问它
export default React.memo(Tiptap, (prevProps, nextProps) => {
  // 比较 defaultContent - 只有当内容真正改变时才返回 false（需要重新渲染）
  const prevContent = prevProps.defaultContent || ''
  const nextContent = nextProps.defaultContent || ''
  const contentChanged = prevContent !== nextContent
  const onChangeChanged = prevProps.onChange !== nextProps.onChange

  if (onChangeChanged) {
    console.log('onChange changed, should rerender!!!!!!!!!!!!!!!!!!!')
  }

  return !contentChanged && !onChangeChanged
})
